import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { loadAvailableDates, productScheduleSelector } from '@src/stores/product';
import { MonthTuple, changeMonth, dateToObj, initialMonth } from '@src/common/calendar';
import { computeVisibleRange } from '@src/domains/scheduling/core/computeVisibleRange';
import { shouldAdvanceAutoLoad } from '@src/domains/scheduling/core/shouldAdvanceAutoLoad';

const AUTO_LOAD_LIMIT = 5;

type UseScheduleDatesLoaderParams = {
  productId: string | undefined;
  /** Seeds the initial visible month. Pass enableAutoLoad=false alongside to keep the seeded month. */
  seedDate?: Date | null;
  /** Defaults to true. Pass false to disable the forward auto-advance loop. */
  enableAutoLoad?: boolean;
  /**
   * Master gate for any fetching. Defaults to true. Pass false to hold off until the caller is
   * ready (e.g. the product object has actually loaded into state) — keeps both the per-month
   * fetch and the forward auto-load from firing in the loading window.
   */
  enabled?: boolean;
};

type ProductScheduleState = ReturnType<ReturnType<typeof productScheduleSelector>>;

type UseScheduleDatesLoaderReturn = {
  monthTuple: MonthTuple;
  setMonthTuple: (next: MonthTuple) => void;
  dates: ProductScheduleState['dates'];
  pending: boolean;
};

/**
 * Owns the date-loading state machine shared by the v1 Calendar and the v2 DateSelector:
 * a visible `monthTuple`, a bounded forward auto-load loop, a per-product reset, and the
 * `loadAvailableDates` dispatch for the visible range.
 *
 * Auto-load advances forward over months that successfully resolve with no schedulings. It is
 * gated on the visible month's *own* range key landing in `loadedKeys`, so it never advances off
 * the shared `pending` flag being flipped by a concurrent load (e.g. loadDefaultTariffGroup in the
 * same v2 card) before the visible month itself resolves. `monthTuple` is read via a ref (not a
 * dep) so a single advance doesn't retrigger the effect in the same commit and skip every attempt.
 */
export function useScheduleDatesLoader({
  productId,
  seedDate,
  enableAutoLoad = true,
  enabled = true,
}: UseScheduleDatesLoaderParams): UseScheduleDatesLoaderReturn {
  const dispatch = useAppDispatch();
  const { dates, pending, loadedKeys } = useAppSelector(productScheduleSelector(productId ?? ''));

  const initialTuple = useMemo<MonthTuple>(() => {
    if (seedDate) {
      const [y, m] = dateToObj(seedDate);
      return [y, m];
    }
    return initialMonth();
  }, [seedDate]);

  const [monthTuple, setMonthTuple] = useState<MonthTuple>(initialTuple);
  const autoLoadCountRef = useRef(AUTO_LOAD_LIMIT);

  // Combined reset + fetch: on productId change, reset to initialTuple and fetch it (skipping
  // a stale fetch from the previous month). Otherwise fetch the current month. initialTuple is
  // read via a ref so re-deriving it (e.g. seedDate change) doesn't dispatch a duplicate fetch.
  const prevProductIdRef = useRef(productId);
  const initialTupleRef = useRef(initialTuple);
  initialTupleRef.current = initialTuple;
  const skipAutoLoadRef = useRef(false);
  useEffect(() => {
    const productChanged = prevProductIdRef.current !== productId;
    prevProductIdRef.current = productId;

    if (!productId || !enabled) return;

    if (productChanged) {
      const nextInitial = initialTupleRef.current;
      setMonthTuple(nextInitial);
      autoLoadCountRef.current = AUTO_LOAD_LIMIT;
      skipAutoLoadRef.current = true;
      const range = computeVisibleRange(nextInitial);
      dispatch(loadAvailableDates(productId, range.dateFrom, range.dateTo));
      return;
    }

    const range = computeVisibleRange(monthTuple);
    dispatch(loadAvailableDates(productId, range.dateFrom, range.dateTo));
  }, [productId, enabled, monthTuple, dispatch]);

  // Auto-load: advance up to AUTO_LOAD_LIMIT forward months while dates stay empty. Gated on the
  // *visible month's own* range key being in loadedKeys, so we only advance after that month's
  // fetch has actually settled with no results. This is essential because `pending` is a single
  // flag shared across the product's schedule entry — a concurrent thunk (e.g. loadDefaultTariffGroup
  // loading in the same v2 card) flips it to false mid-fetch; without the key gate the auto-load
  // would advance off that sibling's settle before the visible month's data arrives. monthTuple is
  // read via a ref so a single advance doesn't retrigger the effect in the same commit; the re-run
  // signal is the fetch settling (loadedKeys/dates/pending change).
  const hasDates = Object.keys(dates ?? {}).length > 0;
  const monthTupleRef = useRef(monthTuple);
  monthTupleRef.current = monthTuple;
  useEffect(() => {
    if (skipAutoLoadRef.current) {
      skipAutoLoadRef.current = false;
      return;
    }
    const range = computeVisibleRange(monthTupleRef.current);
    const key = [range.dateFrom, range.dateTo].join('-');
    if (!loadedKeys?.[key]) return;
    const advance = shouldAdvanceAutoLoad({
      enableAutoLoad: Boolean(productId) && enabled && enableAutoLoad,
      remainingAttempts: autoLoadCountRef.current,
      pending,
      hasDates,
    });
    if (advance) {
      autoLoadCountRef.current -= 1;
      setMonthTuple(changeMonth(monthTupleRef.current, 1));
    }
  }, [productId, enabled, enableAutoLoad, dates, pending, hasDates, loadedKeys]);

  return { monthTuple, setMonthTuple, dates, pending };
}
